'use client'

import { useEffect, useMemo, useRef, type FormEvent } from 'react'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import { videoFunnelEnvelope, videoReferrerType, type VideoFunnelEnvelope } from '@/components/santa-video/video-analytics'
import { EMAIL_FIELD_ID, STEP_KEYS, type StepError, type StepKey, type WizardStep } from '@/components/santa-video/wizard/wizard-steps'
import type { VideoLocale } from '@/server/domain/santa-video/locale'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

/**
 * Qué campo y qué tipo de fallo bloqueó un paso, con identificadores cerrados.
 * Ni el valor escrito ni el texto del aviso. Sólo los códigos que algún campo
 * puede producir hoy: se amplía cuando llegue un campo que produzca otro.
 */
type ValidationError = {
  field: 'child_name' | 'child_age' | 'personal_details' | 'interests' | 'email'
  error_code: 'required' | 'out_of_range' | 'too_few_entries' | 'invalid_format' | 'invalid'
}

const STEP_ERRORS: Record<StepError, ValidationError> = {
  name: { field: 'child_name', error_code: 'required' },
  // `validateStep` pide un entero dentro de `AGE_RANGE`: un decimal tampoco está en el rango de edades aceptadas.
  age: { field: 'child_age', error_code: 'out_of_range' },
  details: { field: 'personal_details', error_code: 'too_few_entries' },
  // Sólo el libro: sin reto pide dos intereses.
  interests: { field: 'interests', error_code: 'too_few_entries' },
}

/** Los campos que valida el navegador en el último paso. El resto (si algún día lo hay) no se cuenta. */
const NATIVE_FIELDS: Record<string, ValidationError['field']> = { [EMAIL_FIELD_ID]: 'email' }

/**
 * El error de un `invalid` del navegador, si viene de un campo conocido. El
 * código sale de los flags de `validity`, nunca de `validationMessage`; el
 * email sólo puede faltar o no tener forma de email.
 */
export function nativeValidationError(target: EventTarget | null): ValidationError | null {
  if (!(target instanceof HTMLInputElement)) return null
  const field = NATIVE_FIELDS[target.id]
  if (!field) return null
  const { valueMissing, typeMismatch } = target.validity
  return { field, error_code: valueMissing ? 'required' : typeMismatch ? 'invalid_format' : 'invalid' }
}

/**
 * Toda la analítica del lado del formulario del wizard (el pago es de
 * `useCheckoutSubmit`). El componente cuenta lo que pasa; aquí se decide cómo
 * se cuenta y con qué deduplicación. Sólo mide: no cambia ni el paso ni la
 * validación.
 *
 * - `started`: la primera interacción, una vez por montaje.
 * - `step_viewed`: sale del estado `step` y sólo en la instancia ya hidratada
 *   (`hydrated`): el render del servidor y el remonte por `key` son dos
 *   montajes del mismo paso. `lastViewed` absorbe el doble efecto del
 *   StrictMode sin dejar de contar la vuelta a un paso ya visto.
 * - `step_completed`: una vez por paso aunque se vuelva atrás.
 * - `validation_error`: cada intento bloqueado, del wizard (pasos 1-2) o del
 *   navegador (último paso, por el `onInvalid` del formulario: el sintético de
 *   React sí burbujea desde los inputs).
 *
 * Todos llevan la historia (`story`): el embudo de la carta y el del libro se
 * separan por propiedad, no por nombre de evento.
 */
export function useWizardAnalytics({ step, locale, story, hydrated }: { step: WizardStep; locale: VideoLocale; story: VideoStoryId; hydrated: boolean }) {
  const envelope = useMemo(() => videoFunnelEnvelope(story, locale), [story, locale])
  const started = useRef(false)
  const completedSteps = useRef(new Set<WizardStep>())
  const lastViewed = useRef<WizardStep | null>(null)
  const stepKey = STEP_KEYS[step]

  useEffect(() => {
    if (!hydrated || lastViewed.current === step) return
    lastViewed.current = step
    captureStepEvent(PRODUCT_EVENTS.VIDEO_FORM_STEP_VIEWED, envelope, stepKey)
  }, [hydrated, step, stepKey, envelope])

  return {
    formStarted() {
      if (started.current) return
      started.current = true
      captureProductEvent(PRODUCT_EVENTS.VIDEO_FORM_STARTED, {
        ...envelope, referrer_type: videoReferrerType(document.referrer, window.location.origin),
      })
    },
    stepCompleted() {
      if (completedSteps.current.has(step)) return
      completedSteps.current.add(step)
      captureStepEvent(PRODUCT_EVENTS.VIDEO_FORM_STEP_COMPLETED, envelope, stepKey)
    },
    /** El wizard enseña el aviso de un paso: se cuenta el campo y el código, nunca el texto. */
    stepErrorShown(error: StepError) {
      captureStepEvent(PRODUCT_EVENTS.VIDEO_FORM_VALIDATION_ERROR, envelope, stepKey, STEP_ERRORS[error])
    },
    onInvalid(event: FormEvent<HTMLFormElement>) {
      const error = nativeValidationError(event.target)
      if (error) captureStepEvent(PRODUCT_EVENTS.VIDEO_FORM_VALIDATION_ERROR, envelope, stepKey, error)
    },
  }
}

type StepEvent =
  | typeof PRODUCT_EVENTS.VIDEO_FORM_STEP_VIEWED
  | typeof PRODUCT_EVENTS.VIDEO_FORM_STEP_COMPLETED
  | typeof PRODUCT_EVENTS.VIDEO_FORM_VALIDATION_ERROR

/** Los eventos de paso: el sobre común más `step` (y el error, si lo hay). */
function captureStepEvent(event: StepEvent, envelope: VideoFunnelEnvelope, step: StepKey, extra?: ValidationError) {
  captureProductEvent(event, { ...envelope, step, ...extra })
}
